import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Container, IconArrowUpRight } from './ui';
import { KUON_ACCENT, KUON_ON_ACCENT, MARK_SQUARE, MARK_GLYPHS, WORDMARK_LETTERS, RING } from './KuonLogo';

// Studio block right above the footer on every page: the KUON logo plus a short pitch
// ("liked how this site works? we built it"), services and a call to action.
// Drawn in the theme's own colors; the square KUON mark is the single accent. Every time it
// comes into view it plays the kuon.com.ua logo intro (choreography, curves and offsets from
// that site) with a quicker loader and a slower, softer settle, then the pitch rises in:
//  1. the mark stamps in and its letters rise one by one;
//  2. a large thin ring loads around it, turning as it draws (0.4s);
//  3. the ring glides into place as the O and thickens while the mark slides left,
//     and K U N arrive (0.9s, long gentle landing).

const STUDIO_URL = 'https://kuon.com.ua';
const CONTACT_URL = 'https://kuon.com.ua/contacts';

// Lockup geometry in em, identical to kuon.com.ua (mark 1.4em, gap 0.5em, wordmark 0.85em tall).
const WORD_WIDTH = '3.0657954545454547em';
const SEAL_SHIFT = '1.7828977272727273em';
const RING_LEFT = '1.419886363636364em';
const RING_DX = '-1.2619886363636366em';
const RING_SCALE = 3.7676842889054356;
const RING_THIN = 3.16;
const MARK_GLYPH_DELAYS = ['0.16s', '0.21s', '0.26s', '0.31s'];
const LETTERS = [
  { delay: '0.85s', from: '-0.25em' },
  { delay: '0.92s', from: '-0.25em' },
  { delay: '0.99s', from: '0.25em' },
];

// Loader, then the morph into the logo (soft ease-in, long ease-out landing).
const LOAD = '0.4s cubic-bezier(0.45, 0, 0.25, 1) 0.1s';
const SETTLE = '0.9s cubic-bezier(0.5, 0, 0.1, 1) 0.5s';

const stamp = keyframes`
  0% { opacity: 0; transform: scale(1.6) rotate(-5deg); filter: blur(10px); }
  55% { opacity: 1; transform: scale(0.95) rotate(0deg); filter: blur(0); }
  to { opacity: 1; transform: none; filter: none; }
`;

const glyphIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

const sealSlide = keyframes`
  0% { transform: translateX(var(--shift)); }
  to { transform: none; }
`;

const letterIn = keyframes`
  0% { opacity: 0; transform: translateX(var(--from)); }
  to { opacity: 1; transform: none; }
`;

const ringLoad = keyframes`
  0% { stroke-dashoffset: var(--arc); transform: rotate(-240deg); }
  to { stroke-dashoffset: 0; transform: none; }
`;

const ringWeight = keyframes`
  0% { stroke-width: var(--thin); }
  to { stroke-width: var(--thick); }
`;

const ringMove = keyframes`
  0% { transform: translateX(var(--dx)) scale(var(--k)); }
  to { transform: none; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Its own block between the page content and the footer, opened by a hairline.
const Band = styled.section`
  padding-bottom: clamp(48px, 6vw, 72px);
`;

const Frame = styled.div`
  padding-top: 32px;
  border-top: 1px solid var(--separator);
`;

const rise = keyframes`
  0% { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: none; }
`;

// Logo on the left, the pitch on the right; stacked on narrow screens.
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 480px);
  align-items: center;
  gap: 28px 56px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }

  /* Nothing shows until the intro starts; each part then enters from its first keyframe. */
  &[data-play='false'] .intro {
    visibility: hidden;
  }

  /* After the logo has formed, the pitch rises in line by line. */
  &[data-play='true'] .reveal {
    animation: ${rise} 700ms cubic-bezier(0.22, 1, 0.36, 1) var(--d, 1s) both;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-play='true'] .reveal {
      animation: ${fadeIn} 300ms ease both;
    }
  }
`;

const LogoLink = styled.a`
  display: inline-flex;
  color: var(--text);
  text-decoration: none;
  border-radius: 12px;
  -webkit-tap-highlight-color: transparent;
`;

const Pitch = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Headline = styled.p`
  font-family: var(--font-display);
  font-size: clamp(1.375rem, 2.4vw, 1.75rem);
  font-weight: 650;
  line-height: 1.2;
  letter-spacing: -0.022em;
  color: var(--text-tertiary);
  text-wrap: balance;

  strong {
    font-weight: inherit;
    color: var(--text);
  }
`;

const Text = styled.p`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--text-secondary);
  text-wrap: pretty;
`;

const Services = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  li {
    padding: 4px 11px;
    border-radius: 980px;
    border: 1px solid var(--separator-strong);
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-top: 4px;
`;

const SiteLink = styled.a`
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 200ms ease;

  &:hover {
    color: var(--text);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const Label = styled.span`
  display: block;
  margin-bottom: 14px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
`;

const Lockup = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: clamp(34px, 6vw, 60px);
  line-height: 1;

  .seal-slide {
    position: relative;
    z-index: 1;
    display: block;
  }

  .seal {
    display: block;
    height: 1.4em;
    width: auto;
  }

  .word {
    position: relative;
    display: block;
    height: 0.85em;
    width: ${WORD_WIDTH};

    > svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  .ring-move {
    position: absolute;
    top: 0;
    left: ${RING_LEFT};
    width: 0.85em;
    height: 100%;
    transform-origin: center;

    svg {
      display: block;
      width: 100%;
      height: 100%;
      overflow: visible;
    }
  }

  .ring {
    transform-box: fill-box;
    transform-origin: center;
  }

  [data-play='true'] & .seal-slide {
    animation: ${sealSlide} ${SETTLE} both;
  }

  [data-play='true'] & .seal {
    animation: ${stamp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) 0.02s both;
  }

  [data-play='true'] & .glyph {
    animation: ${glyphIn} 0.26s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  [data-play='true'] & .letter {
    animation: ${letterIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  [data-play='true'] & .ring-move {
    animation: ${ringMove} ${SETTLE} both;
  }

  [data-play='true'] & .ring {
    animation:
      ${ringLoad} ${LOAD} both,
      ${ringWeight} ${SETTLE} both;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-play='true'] & .seal-slide,
    [data-play='true'] & .seal,
    [data-play='true'] & .glyph,
    [data-play='true'] & .letter,
    [data-play='true'] & .ring-move,
    [data-play='true'] & .ring {
      animation: ${fadeIn} 300ms ease both;
    }
  }
`;

// ------------------------------------------------------------------ call to action

const ArrowBox = styled.span`
  position: relative;
  display: inline-block;
  width: 16px;
  height: 16px;
  overflow: hidden;

  svg {
    position: absolute;
    inset: 0;
    transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  svg + svg {
    transform: translate(-120%, 120%);
  }
`;

// Solid in the text color (dark on light, light on dark); on hover the arrow slips out
// to the top-right while a fresh one slides in from the bottom-left.
const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 18px 0 22px;
  border-radius: 980px;
  background: var(--text);
  color: var(--bg);
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none;
  transition:
    opacity 200ms ease,
    transform 180ms var(--ease-out);
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover {
      opacity: 0.88;
    }

    &:hover ${ArrowBox} svg:first-child {
      transform: translate(120%, -120%);
    }

    &:hover ${ArrowBox} svg + svg {
      transform: none;
    }
  }

  &:active {
    transform: scale(0.97);
    transition-duration: 80ms;
  }

  @media (prefers-reduced-motion: reduce) {
    ${ArrowBox} svg {
      transition: none;
    }
  }
`;

// ------------------------------------------------------------------ component

// Plays when at least half of the signature is visible; resets only once it has left the
// screen completely, so the reset is never seen and edge-hovering doesn't retrigger it.
function useReplayOnView() {
  const ref = useRef(null);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    if (!('IntersectionObserver' in window)) {
      setPlay(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) setPlay(true);
        else if (!entry.isIntersecting) setPlay(false);
      },
      { threshold: [0, 0.5] }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, play];
}

function DeveloperCredit({ locale }) {
  const [lineRef, play] = useReplayOnView();

  return (
    <Band aria-label={locale.label}>
      <Container>
        <Frame>
          <Grid ref={lineRef} data-play={play}>
            <div>
              <Label className="intro reveal" style={{ '--d': '1.15s' }}>
                {locale.label}
              </Label>
              <LogoLink href={STUDIO_URL} target="_blank" rel="noopener" aria-label={locale.aria}>
                <Lockup className="intro" aria-hidden="true">
                  <span className="seal-slide" style={{ '--shift': SEAL_SHIFT }}>
                    <svg className="seal" viewBox="0 0 100 100" focusable="false">
                      <path d={MARK_SQUARE} fill={KUON_ACCENT} />
                      {MARK_GLYPHS.map((glyph, index) => (
                        <path
                          key={MARK_GLYPH_DELAYS[index]}
                          className="glyph"
                          style={{ animationDelay: MARK_GLYPH_DELAYS[index] }}
                          d={glyph}
                          fill={KUON_ON_ACCENT}
                        />
                      ))}
                    </svg>
                  </span>
                  <span className="word">
                    <svg viewBox="0 -0.6 365.01 101.2" focusable="false">
                      {WORDMARK_LETTERS.map((letter, index) => (
                        <path
                          key={LETTERS[index].delay}
                          className="letter"
                          style={{ animationDelay: LETTERS[index].delay, '--from': LETTERS[index].from }}
                          d={letter}
                          fill="currentColor"
                        />
                      ))}
                    </svg>
                    <span className="ring-move" style={{ '--dx': RING_DX, '--k': RING_SCALE }}>
                      <svg viewBox={`${RING.cx - 50.6} -0.6 101.2 101.2`} focusable="false">
                        <circle
                          className="ring"
                          cx={RING.cx}
                          cy={RING.cy}
                          r={RING.r}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={RING.strokeWidth}
                          strokeLinecap="round"
                          strokeDasharray={`${RING.arc} ${RING.gap}`}
                          style={{ '--arc': RING.arc, '--thin': RING_THIN, '--thick': RING.strokeWidth }}
                        />
                      </svg>
                    </span>
                  </span>
                </Lockup>
              </LogoLink>
            </div>

            <Pitch>
              <Headline className="intro reveal" style={{ '--d': '0.95s' }}>
                {locale.question} <strong>{locale.answer}</strong>
              </Headline>
              <Text className="intro reveal" style={{ '--d': '1.05s' }}>
                {locale.text}
              </Text>
              <Services className="intro reveal" style={{ '--d': '1.15s' }} aria-label={locale.label}>
                {locale.services.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </Services>
              <Actions className="intro reveal" style={{ '--d': '1.25s' }}>
                <Cta href={CONTACT_URL} target="_blank" rel="noopener">
                  {locale.cta}
                  <ArrowBox aria-hidden="true">
                    <IconArrowUpRight size={16} strokeWidth={2.25} />
                    <IconArrowUpRight size={16} strokeWidth={2.25} />
                  </ArrowBox>
                </Cta>
                <SiteLink href={STUDIO_URL} target="_blank" rel="noopener">
                  kuon.com.ua
                </SiteLink>
              </Actions>
            </Pitch>
          </Grid>
        </Frame>
      </Container>
    </Band>
  );
}

export default DeveloperCredit;
